import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuctionFeedResponse } from '@marstein-radar/shared';
import { tableStyle, thStyle, tdStyle, buttonStyle } from '../styles';

const AUCTION_TYPE_LABEL: Record<string, string> = {
  exekucna: 'Exekučná dražba',
  dobrovolna: 'Dobrovoľná dražba',
};

// Dražby z Obchodného vestníka (Týž. 21, plán §B2) — priamy prieskum
// Slovensko.Digital datahub `ov/raw_issues` feedu 2026-07-18 zistil, že
// dražobné vyhlášky (exekučné aj dobrovoľné) sú v ňom priamo dostupné bez
// registrácie/čakania na ministerstvo (na rozdiel od štruktúrovaného
// denného OV exportu, ktorý appka nemá). Kostra kopíruje
// InsolvencyFeedPage.tsx (etablovaný vzor filtrovateľného feedu).
export function AuctionFeedPage() {
  const { data: regions } = useQuery<string[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/regions');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  const [region, setRegion] = useState('');
  const [auctionType, setAuctionType] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [submittedKey, setSubmittedKey] = useState<string | null>('initial');

  const { data, isFetching } = useQuery<AuctionFeedResponse>({
    queryKey: ['auction-feed', submittedKey, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (region) params.set('region', region);
      if (auctionType) params.set('auctionType', auctionType);
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      const res = await fetch(`/api/v1/auctions/feed?${params.toString()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: submittedKey !== null,
  });

  function submit(): void {
    setPage(1);
    setSubmittedKey(`${region}|${auctionType}|${q}|${Date.now()}`);
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <main className="page">
      <div className="page-header">
        <h1>Dražby</h1>
        <p>Exekučné aj dobrovoľné dražby z Obchodného vestníka — čo sa práve predáva.</p>
      </div>

      <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
        Zdroj: surové podania Obchodného vestníka (Slovensko.Digital datahub). Pri
        <strong> exekučnej</strong> dražbe je kraj/okres priamo z formulára, pri{' '}
        <strong>dobrovoľnej</strong> dražbe len obec sídla dražobníka — presnú adresu predávanej
        nehnuteľnosti nájdeš v popise predmetu nižšie. Údaje sú informatívne, nie právne záväzné.
      </div>

      <div className="search-filter-panel">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="filter-grid">
            <label className="filter-field">
              <span>Typ dražby</span>
              <select value={auctionType} onChange={(e) => setAuctionType(e.target.value)}>
                <option value="">Všetky typy</option>
                {Object.entries(AUCTION_TYPE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field">
              <span>Kraj</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="">Všetky kraje</option>
                {regions?.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field filter-field-wide">
              <span>Hľadať v popise / dražobníkovi</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="napr. rodinný dom, byt, Bratislava..."
              />
            </label>
          </div>
          <button type="submit" className="search-filter-submit">
            Hľadať
          </button>
        </form>
      </div>

      {isFetching && !data && (
        <div className="fin-panel">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="skel-row" key={i}>
              <div>
                <div className="skel line1" />
                <div className="skel line2" />
              </div>
              <div className="skel pill" />
              <div className="skel num" />
            </div>
          ))}
        </div>
      )}
      {data && data.results.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Žiadne dražby nenájdené pre zadané filtre.
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <div className="section-title">
            <h2>Výsledky</h2>
            <span className="hint">{data.total.toLocaleString('sk-SK')} dražieb spolu</span>
          </div>
          <div className="overflow-x">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Dátum konania</th>
                  <th style={thStyle}>Typ</th>
                  <th style={thStyle}>Kraj / obec</th>
                  <th style={thStyle}>Predmet</th>
                  <th style={thStyle}>Dražobník / exekútor</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle} className="num">
                      {r.auctionDate ?? '—'}
                    </td>
                    <td style={tdStyle}>{AUCTION_TYPE_LABEL[r.auctionType] ?? r.auctionType}</td>
                    <td style={tdStyle}>
                      {[r.region, r.district, r.municipality].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 420 }}>
                      {r.predmet ?? r.propertyType ?? '—'}
                    </td>
                    <td style={tdStyle}>{r.organizerName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem' }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ ...buttonStyle, opacity: page <= 1 ? 0.5 : 1 }}
              >
                ← Predošlá
              </button>
              <span style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                Strana {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ ...buttonStyle, opacity: page >= totalPages ? 0.5 : 1 }}
              >
                Ďalšia →
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
